import { useState } from "react";
import {
  ArrowsPointingInIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  PhoneIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { cn } from "@utils";
import { toast } from "@components/toast/Toast";
import Tooltip from "@components/Tooltip";
import { PayerTypeLabel, WatchFlagChips } from "@areas/trade-accounts/components/FactCells";
import ScoreRing from "./ScoreRing";
import { SINGLE_LINE_TOOLTIP } from "./documentActions";

// A contact value (email / phone) shown as a compact chip (matching the watch-flag pills); clicking
// copies it, with the trailing copy glyph flipping to a tick for a moment as confirmation.
function ContactChip({ icon: Icon, value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      toast({ description: `${label} copied` });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked — nothing to copy to, so stay quiet
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy ${label.toLowerCase()}`}
      className="group inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border border-zinc-200 bg-white px-2 py-0.5 text-xs font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:text-zinc-900"
    >
      <Icon aria-hidden="true" className="size-3.5 text-zinc-400" />
      <span>{value}</span>
      {copied ? (
        <CheckIcon aria-hidden="true" className="size-3.5 text-green-600" strokeWidth={2.5} />
      ) : (
        <ClipboardDocumentIcon
          aria-hidden="true"
          className="size-3.5 text-zinc-400 group-hover:text-zinc-600"
        />
      )}
    </button>
  );
}

export default function AccountHeader({
  customer,
  detail,
  onClose,
  onAddNote,
  onRequestPayment,
  onBackToSearch,
}) {
  const name = detail?.name ?? customer?.name;
  const accountCode = detail?.accountCode ?? customer?.accountCode;
  const creditCode = detail?.creditCode;
  const score = detail?.performanceScore ?? customer?.performanceScore;
  const email = detail?.email ?? customer?.email;
  const phone = detail?.phone ?? customer?.phone;

  const hasStatus =
    detail?.activityState ||
    detail?.timingPersona ||
    detail?.watchFlags?.length > 0;

  return (
    <div className="border-b border-zinc-200 px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {typeof score === "number" && <ScoreRing score={score} />}
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
            <h2 className="truncate text-2xl font-semibold text-zinc-900">{name}</h2>
            {accountCode && (
              <span className="text-sm font-medium text-zinc-400">{accountCode}</span>
            )}
            {creditCode && (
              <span className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700">
                {creditCode}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onAddNote}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            <PlusIcon aria-hidden="true" className="size-4" />
            Add note
          </button>

          <button
            type="button"
            onClick={onRequestPayment}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-900"
          >
            <PaperAirplaneIcon aria-hidden="true" className="size-4" />
            Request payment
          </button>

          <Tooltip
            content="Collapse to account search"
            placement="bottom"
            wrapperClassName="ml-1 flex"
            className={SINGLE_LINE_TOOLTIP}
          >
            <button
              type="button"
              onClick={onBackToSearch}
              aria-label="Collapse to account search"
              className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
              <ArrowsPointingInIcon aria-hidden="true" className="size-5" />
            </button>
          </Tooltip>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            aria-label="Close"
          >
            <XMarkIcon aria-hidden="true" className="size-6" />
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ContactChip icon={EnvelopeIcon} value={email} label="Email" />
        <ContactChip icon={PhoneIcon} value={phone} label="Phone" />
        {hasStatus && (
          <div className={cn("flex flex-wrap items-center gap-2", (email || phone) && "sm:ml-1")}>
            {(detail?.activityState || detail?.timingPersona) && (
              <PayerTypeLabel
                activityState={detail.activityState}
                timingPersona={detail.timingPersona}
                lateBand={detail.lateBand}
                detail={detail.payerTypeDetail}
              />
            )}
            {detail?.watchFlags?.length > 0 && (
              <WatchFlagChips flags={detail.watchFlags} details={detail.watchFlagDetails} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
